/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT, propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../../../util/urlHelpers';
import { getUserPlan } from '../../../../util/getUserPlan';
import { FaHeart, FaLock } from 'react-icons/fa';
import { isReactNativeWrappedApp } from '../../../../util/reactNativeApp';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  // Check if the link is the "Get the App" link and if we are in the app wrapper
  if (isReactNativeWrappedApp() && href === '/p/my-tattoo-hub-app') {
    return null;
  }

  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
    isClientAc,
    currentUserListing,
    isOpenPixSubAcCreated,
    currentUserListingFetched,
  } = props; 
  const currentUserHasListing = currentUserListing && currentUserListingFetched;
  const history = useHistory();
  const id = currentUserListing?.id?.uuid || '00000000-0000-0000-0000-000000000000';
  const { title = '', price, state, publicData } = currentUserListing?.attributes || {};
  const slug = createSlug(title) || 'draft';
  const isDraft = state === LISTING_STATE_DRAFT;

  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const user = ensureCurrentUser(currentUser);

  const { hasActivePlan } = getUserPlan(user) || {};

  const extraLinks = customLinks.map((linkConfig, index) => {
      return (
        <CustomLinkComponent
          key={`${linkConfig.text}_${index}`}
          linkConfig={linkConfig}
          currentPage={currentPage}
        />
      );
    });

  if (isAuthenticated && isClientAc) {
    const flashLinkIndex = customLinks.findIndex(
      link => link.route?.name === 'FlashSearchPage' || link.href === '/flash'
    );
    extraLinks.splice(
      flashLinkIndex < 0 ? extraLinks.length : flashLinkIndex + 1,
      0,
      <NamedLink
        key="favorites"
        name="FavoritesPage"
        className={classNames(css.navigationLink, css.favoritesNavigationLink, {
          [css.currentPage]: currentPage === 'FavoritesPage',
        })}
      >
        <FormattedMessage id="SearchPage.favorites" />
        <FaHeart className={css.favoritesIcon} aria-hidden="true" />
      </NamedLink>
    );
  }

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    const isForTattooArtisPage = history?.location.pathname === '/p/my-tattoo-hub-para-tatuadores';
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div
            className={classNames(css.authenticationGreeting, {
              [css.appAuthenticationGreeting]: !!isReactNativeWrappedApp(),
            })}
          >
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={classNames(css.customLinksWrapper,css.extraLinksWrapper)}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        {/* {!isAuthenticated ? (
          isForTattooArtisPage ? (
            <div className={css.footer}>
              <NamedLink className={css.createNewListingLink} name="NewListingPage">
                <FormattedMessage id="TopbarMobileMenu.newListingLink" />
              </NamedLink>
            </div>
          ) : null
        ) : (
          <div className={css.footer}>
            <NamedLink className={css.createNewListingLink} name="NewListingPage">
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            </NamedLink>
          </div>
        )} */}

        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountDetailsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  // const inboxTab = currentUserHasListings ? 'sales' : 'orders';
  const inboxTab = isClientAc ? 'orders' : 'sales';

  return (
    <div className={css.root}>
      <AvatarLarge
        className={classNames(css.avatar, { [css.appAvatar]: !!isReactNativeWrappedApp() })}
        user={currentUser}
      />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {!isClientAc && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          )}
          {!isClientAc && (
            <NamedLink
              className={css.navigationLink}
              name="ProfileSettingsPage"
              to={{ hash: '#link-pessoal' }}
            >
              <FormattedMessage id="TopbarMobileMenu.personalLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountDetailsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>

          {currentUserHasListings && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('EditListingPage'))}
              name="EditListingPage"
              params={{ id, slug, type: editListingLinkType, tab: 'availability' }}
            >
              <FormattedMessage id="TopbarMobileMenu.calenderAvailability" />
            </NamedLink>
          )}

          {!isClientAc && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SubscriptionPage'))}
              name="SubscriptionPage"
            >
              <FormattedMessage id="TopbarDesktop.subscription" />
            </NamedLink>
          )}

          {!isClientAc && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ActivityDashboardPage'), {
                [css.disabledLink]: !hasActivePlan,
              })}
              name="ActivityDashboardPage"
            >
              <FormattedMessage id="TopbarMobileMenu.dashboard" />
              {!hasActivePlan && (
                <span className={css.iconContainer}>
                  <FaLock className={css.icon} />
                </span>
              )}
            </NamedLink>
          )}
        </div>
        <div className={classNames(css.customLinksWrapper,css.extraLinksWrapper)}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      {/* {!!isClientAc || (!!currentUserListing && !!isOpenPixSubAcCreated) ? null : ( */}

      {!!isClientAc || !!isOpenPixSubAcCreated ? null : !currentUserHasListing && !hasActivePlan ? (
        <div className={css.footer}>
          <NamedLink
            className={css.createNewListingLink}
            name="SubscriptionPage"
            to={{
              state: { from: 'newListingpage' },
            }}
          >
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      ) : (
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      )}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
